import { MouseEvent, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';

import { Props, ContentLinkProps } from './content-card.constants';
import {
  createExternalUrl,
  getScreenReaderCardLinkText,
  goOrShowModal,
  openLinkAsAuthenticatedUser,
  shouldOpenInNewTab,
} from './content-card.utils';
import { useGenerateItemLink } from './content-card.hooks';
import Reorder from './reorder/reorder';
import Delete from './delete/delete';
import {
  ThumbnailReorderWrapper,
  MoveLabelStyled,
  EditMask,
} from 'approot/shared/styles/thumbnails.style';
import { pushDataTrackerEvent } from 'approot/shared/data-tracker/data-tracker-push';
import { DIRECTION_LEFT, DIRECTION_RIGHT } from 'webclient.constants';
import { InlineServerError } from 'approot/account/server-error';
import ConfirmModal, {
  useConfirmModalState,
} from '../confirm-modal/confirm-modal';
import useUserData from 'approot/shared/user/user-data';
import { PStyled } from 'approot/shared/styles/typography.style';
import { SecondaryButton } from 'approot/shared/styles/buttons/buttons';
import { isKeyboardModifierClick } from 'lib/browser';
import { convert2Array } from 'lib/arrays/arrays';
import { ActionButtonWrapper } from 'approot/shared/action-button/action-button';
import {
  useImageLoad,
  useResponsiveImage,
} from 'approot/shared/images/images.hooks';
import { ScreenReaderOnly } from 'approot/shared/styles/global.styles';
import * as Styled from './content-card.styles';

type ContentCardImageProps = {
  imageUrl?: string;
  ratio?: number;
  ignoreImageOrientation?: boolean;
  hasDarkOverlay?: boolean;
  overlayText?: string;
};

const ContentCardImage = ({
  imageUrl,
  ignoreImageOrientation,
  hasDarkOverlay,
  overlayText,
  ...props
}: ContentCardImageProps) => {
  const { imageHasLoaded, isLandscape } = useImageLoad(imageUrl);
  return (
    <>
      <Styled.ContentCardImage
        imageHasLoaded={imageHasLoaded}
        imageUrl={imageUrl}
        isLandscape={isLandscape}
        ignoreImageOrientation={ignoreImageOrientation}
        {...props}
      />
      {hasDarkOverlay && (
        <Styled.ContentCardDarkOverlayStyled>
          {overlayText && (
            <Styled.ContentCardOverlayTextStyled data-selector="content-card-overlay-text">
              {overlayText}
            </Styled.ContentCardOverlayTextStyled>
          )}
        </Styled.ContentCardDarkOverlayStyled>
      )}
    </>
  );
};

const ContentCard = ({
  className,
  pillText,
  title,
  pillIcon,
  detail,
  metaLine,
  metaLineIcon,
  metaLineSecondary,
  metaData,
  images,
  imageRatio,
  imageEditor,
  button,
  buttonSpace,
  dataEvent,
  dataEventAction,
  dataEventLabel,
  actionButtons,
  error,
  type,
  cardInteractions,
  isSelected,
  isAlwaysViewable = false,
  ignoreImageOrientation = false,
  contentColour,
  hasDarkOverlay,
  overlayText,
  featureBadge,
  featureBadgeText,
  slugOverride,
  ...props
}: Props) => {
  const navigate = useNavigate();
  const { openConfirm, closeConfirm, isConfirmVisible } =
    useConfirmModalState();

  const {
    openConfirm: openDisabled,
    closeConfirm: closeDisabled,
    isConfirmVisible: isDisabledVisible,
  } = useConfirmModalState();

  const { canViewPaidContent } = useUserData();

  const item: ContentLinkProps = {
    type: type,
    link: props.link,
    slug: props.slug,
    disabled: props.disabled,
  };
  const href =
    useGenerateItemLink({
      item,
      isAlwaysViewable,
      canOpenStimulus: canViewPaidContent,
      slugOverride,
    }) || '';

  const imageUrl = useResponsiveImage(images || [], 500);

  const handleLinkClick = useCallback(
    (e: MouseEvent<HTMLAnchorElement>) => {
      if (dataEvent && dataEventAction && dataEventLabel) {
        pushDataTrackerEvent(dataEvent, dataEventAction, dataEventLabel);
      }
      if (isKeyboardModifierClick(e)) return;
      if (!goOrShowModal(href)) {
        e.preventDefault();
      }
    },
    [href, dataEvent, dataEventAction, dataEventLabel]
  );

  const handleLinkAsAuthenticatedUser = useCallback(
    (e: MouseEvent<HTMLAnchorElement>) => {
      if (dataEvent && dataEventAction && dataEventLabel) {
        pushDataTrackerEvent(dataEvent, dataEventAction, dataEventLabel);
      }
      openLinkAsAuthenticatedUser({
        event: e,
        href,
        navigate,
      });
    },
    [href, dataEvent, dataEventAction, dataEventLabel, navigate]
  );

  const handleConfirmDelete = useCallback(() => {
    closeConfirm();
    if (props.isEditable && props.isEditing && props.handleDelete) {
      props.handleDelete(props.content.id, props.content.type);
    }
  }, [props, closeConfirm]);

  const isEditing = props.isEditable && props.isEditing;

  const editLayer =
    props.isEditable && props.isEditing ? (
      <Styled.ContentCardLayer ratio={imageRatio}>
        <Styled.ActionButtonContainer>
          <ConfirmModal
            isVisible={isConfirmVisible}
            title={`Are you sure you want to delete ${props.description} with all its contents?`}
            handleClose={closeConfirm}
            handleConfirm={handleConfirmDelete}
          >
            <PStyled textAlignCenter>
              {props.deleteContentMessage
                ? props.deleteContentMessage
                : 'This action cannot be undone.'}
            </PStyled>
          </ConfirmModal>
          <ConfirmModal
            isVisible={isDisabledVisible}
            title={
              props.disableDeleteMessage ||
              `You cannot delete ${props.content.name}.`
            }
            actions={
              <SecondaryButton onClick={() => closeDisabled()}>
                OK
              </SecondaryButton>
            }
            handleClose={closeDisabled}
            handleConfirm={closeDisabled}
          />
          {props.hideDelete || (
            <Delete
              name={props.description}
              content={props.content}
              disabled={props.disableDelete}
              disabledMessage={props.disableDeleteMessage}
              handleDelete={
                props.handleDeleteEvent ? props.handleDeleteEvent : openConfirm
              }
              handleDisabledDelete={openDisabled}
            />
          )}
        </Styled.ActionButtonContainer>

        {/* Can't reorder if there is only a single item */}
        {!(props.isFirst && props.isLast) && (
          <ThumbnailReorderWrapper>
            <Reorder
              buttonTitle={props.description}
              content={props.content}
              direction={DIRECTION_LEFT}
              handleReorder={props.handleReorder}
              disabled={props.isFirst}
            />
            <MoveLabelStyled>Move position</MoveLabelStyled>
            <Reorder
              buttonTitle={props.description}
              content={props.content}
              direction={DIRECTION_RIGHT}
              handleReorder={props.handleReorder}
              disabled={props.isLast}
            />
          </ThumbnailReorderWrapper>
        )}
      </Styled.ContentCardLayer>
    ) : null;

  const cardImage = (
    <Styled.ContentCard
      isDisabled={!href || isEditing}
      ratio={imageRatio}
      contentColour={contentColour}
    >
      {imageEditor ? ( // Allow to overide image with thumbnail picker
        <Styled.ContentCardEditorContainer ratio={imageRatio}>
          {imageEditor}
        </Styled.ContentCardEditorContainer>
      ) : (
        imageUrl && (
          <ContentCardImage
            key={imageUrl}
            imageUrl={imageUrl}
            ratio={imageRatio || 120}
            ignoreImageOrientation={ignoreImageOrientation}
            hasDarkOverlay={hasDarkOverlay}
            overlayText={overlayText}
            data-selector="content-card-image"
          />
        )
      )}
      {isEditing && <EditMask />}
    </Styled.ContentCard>
  );

  const cardScreenReaderLabel = (
    <ScreenReaderOnly>
      {getScreenReaderCardLinkText(
        pillText,
        title,
        detail,
        metaLine,
        featureBadgeText
      )}
    </ScreenReaderOnly>
  );
  const card = (
    <>
      {href && (
        <>
          {shouldOpenInNewTab(href) ? (
            <Styled.ContentCardAnchorStyled
              href={createExternalUrl(href)}
              target="_blank"
              rel="noopener noreferrer"
              onClick={handleLinkAsAuthenticatedUser}
            >
              {cardScreenReaderLabel}
            </Styled.ContentCardAnchorStyled>
          ) : (
            <Styled.ContentCardLinkStyled to={href} onClick={handleLinkClick}>
              {cardScreenReaderLabel}
            </Styled.ContentCardLinkStyled>
          )}
        </>
      )}

      <Styled.ContentCardContent>
        {cardImage}

        {pillText && (
          <Styled.PillContainer
            aria-hidden={!!href && !isEditing}
            isEditor={!!imageEditor}
          >
            <Styled.ContentCardPill data-selector="overlay-pill">
              {pillIcon && <Styled.ContentCardIcon id={pillIcon} />}
              {pillText}
            </Styled.ContentCardPill>
          </Styled.PillContainer>
        )}

        <Styled.Content aria-hidden={!!href}>
          <Styled.ContentCardFeatureBadgeWrapper>
            {featureBadge}
          </Styled.ContentCardFeatureBadgeWrapper>
          <Styled.ContentCardTitle
            withMarginBottom={!!metaLine}
            data-selector="selector-thumbnail-title"
          >
            {title}
          </Styled.ContentCardTitle>
          {detail && detail.length > 0 && (
            <Styled.ContentCardDetail data-selector="content-card-detail">
              {convert2Array(detail).join(' · ')}
            </Styled.ContentCardDetail>
          )}
          {metaLine && metaLine.length > 0 && (
            <Styled.ContentCardMetaLine data-selector="thumbnail-subject-year">
              {metaLineIcon && <Styled.ContentCardIcon id={metaLineIcon} />}
              {convert2Array(metaLine).join(' · ')}
            </Styled.ContentCardMetaLine>
          )}
          {metaLineSecondary && metaLineSecondary.length > 0 && (
            <Styled.ContentCardMetaLineSecondary>
              {convert2Array(metaLineSecondary).join(' · ')}
            </Styled.ContentCardMetaLineSecondary>
          )}
          {metaData && metaData}
        </Styled.Content>

        {cardInteractions}
        {editLayer}

        {actionButtons && (
          <Styled.ContentCardEditActions ratio={imageRatio}>
            <ActionButtonWrapper buttons={actionButtons} />
          </Styled.ContentCardEditActions>
        )}
      </Styled.ContentCardContent>
    </>
  );

  return (
    <Styled.ContentCardWrapper className={className} buttonSpace={buttonSpace}>
      <Styled.ContentCardBox data-selector="content-card-box">
        {card}
      </Styled.ContentCardBox>
      {error && <InlineServerError />}
      {button && <Styled.ContentCardButton {...button} />}
    </Styled.ContentCardWrapper>
  );
};

export default ContentCard;
